<template>
  <section data-cy="pump-row-section">
    <EquipmentRow data-cy="equipment-row">
      <template #left></template>

      <template #default="{ toggle }">
        <AddRemoveButtons
          data-cy="add-remove-buttons"
          :disable-add-above="disableAddAbove"
          :disable-add-below="disableAddBelow"
          :disable-remove="disableRemove"
          :hide-add-above="hideAddAbove"
          :hide-add-below="hideAddBelow"
          :hide-remove="hideRemove"
          @add-above="$emit('add-above')"
          @add-below="$emit('add-below')"
          @remove="$emit('remove', pump)"
        >
          <template #middle-content>
            <EquipmentPanel
              class="flex-grow-1"
              :rule-names="[
                'pump_size_top_casing',
                'pump_size_bottom_casing',
                'pump_size_bypass_tubing_top_casing',
                'pump_size_bypass_tubing_bottom_casing',
                'hal_pump_diameter',
                'gas_lock',
                'pump_outside_graphing_range',
                'pump_outside_operating_range',
                'pump_rate_correction_ratio_below_threshold',
                'pump_head_correction_ratio_below_threshold',
                'pump_bhp_correction_ratio_below_threshold',
                'shut_in_burst_limit_hs',
                'shut_in_burst_limit_std',
                'operating_burst_limit_hs',
                'operating_burst_limit_std',
                'pump_shaft_limit_hs',
                'pump_shaft_limit_std',
                'pump_obsolete',
                'legacy_empty_housings',
                'pump_rotation_mismatch'
              ]"
              :xref-id="pump.id"
              :stack="stack"
              @toggle="toggle"
            >
              <template #header>{{ panelHeader }}</template>
              <template v-if="pump?.r_pump" #trigger>
                <div class="pump-label-wrapper">
                  <span>
                    {{ pumpLabel }}
                  </span>
                  <div class="pump-badge-outer">
                    <div class="pump-badge-inner">
                      <Badge
                        v-if="hasCorrections"
                        class="pump-badge"
                        severity="info"
                        :value="viscosityCorrectionLabel"
                      />
                      <Badge
                        v-if="hasCompression"
                        class="pump-badge"
                        severity="success"
                        value="Compression"
                      />
                    </div>
                  </div>
                </div>
              </template>
            </EquipmentPanel>
          </template>
        </AddRemoveButtons>
      </template>
      <template #expand="{ toggle }">
        <PumpSearchView
          data-cy="pump-search-view"
          :flowrate-bbld="pumpFlowRate"
          :enable-flow-rate-filter="defaultEnableFilter"
          :pump-xref="pump"
          @select="handlePumpSelect(pump, $event, toggle)"
        />
      </template>
      <template #right>
        <div class="flex flex-column">
          <!-- Required/Selected Stages Display -->
          <div
            v-if="pump?.r_pump"
            class="compact-pump-stages-row flex flex-wrap gap-4 mb-2"
          >
            <InlineValue
              title="Required Pump Stages"
              :value="requiredPumpStages"
            />
            <InlineValue
              title="Selected Pump Stages"
              :value="pump?.total_stages || 0"
            />
            <InlineValue
              title="Total Stages (All Pumps in Stack)"
              :value="totalSelectedPumpStages"
            />
          </div>
          <div class="compact-pump-inputs-row flex">
            <div class="compact-pump-input-item">
              <FormLabel v-slot="{ id }" text="Total Stages">
                <InputNumber
                  :id="id"
                  :model-value="pump?.total_stages || 1"
                  :disabled="pumpHasAvailableHousings || !pump?.r_pump"
                  @click.stop
                  @update:model-value="
                    $emit('update:pumps', {
                      ...pump,
                      total_stages: $event
                    })
                  "
                />
              </FormLabel>
            </div>
            <div class="compact-pump-input-item">
              <ConverterInput
                label="Pump Length"
                :base-unit="LengthUnits.Feet"
                :model="LargeLengthUnits.options()"
                mode="write"
                :model-value="pump.length_feet"
                :disabled="pumpHasAvailableHousings || !pump?.r_pump"
                @click.stop
                @update:model-value="
                  $emit('update:pumps', {
                    ...pump,
                    length_feet: $event
                  })
                "
              />
            </div>
            <div class="compact-pump-input-item">
              <ConverterInput
                label="Pump Weight"
                :base-unit="MassUnits.Pounds"
                :model="MassUnits.options()"
                mode="write"
                :model-value="pump.weight_pounds"
                :disabled="pumpHasAvailableHousings || !pump?.r_pump"
                @click.stop
                @update:model-value="
                  $emit('update:pumps', {
                    ...pump,
                    weight_pounds: $event
                  })
                "
              />
            </div>
          </div>
          <div class="flex flex-wrap mt-2 gap-3">
            <div class="field-checkbox mr-2">
              <Checkbox
                v-model="openHousing"
                :input-id="`housing-${pump?.id}`"
                binary
                :disabled="!pumpHasAvailableHousings"
              />
              <label
                :for="`housing-${pump?.id}`"
                :class="{ disabled: !pumpHasAvailableHousings }"
              >
                Show Housing
              </label>
            </div>
            <div class="field-checkbox mr-2">
              <AlertWrapper :rule-names="['viscosity']">
                <Checkbox
                  v-model="openCorrections"
                  :input-id="`corrections-${pump?.id}`"
                  :disabled="!pump?.r_pump"
                  binary
                />
                <label
                  :for="`corrections-${pump?.id}`"
                  :class="{ disabled: !pump?.r_pump }"
                >
                  {{ showCorrectionsLabel }}
                </label>
              </AlertWrapper>
            </div>
            <div class="field-checkbox">
              <AlertWrapper
                :rule-names="['pump_shaft_limit_std']"
                :xref-id="pump.id"
                :stack="stack"
              >
                <Checkbox
                  v-model="useHighStrengthShaft"
                  :disabled="!pump?.r_pump || isScenarioReadonly"
                  :input-id="`high-strength-shaft-${pump?.id}`"
                  binary
                />
                <label
                  :for="`high-strength-shaft-${pump?.id}`"
                  :class="{ disabled: !pumpHasAvailableHousings }"
                >
                  {{ useHighStrengthShaftLabel }}
                </label>
              </AlertWrapper>
            </div>
          </div>
        </div>
      </template>
    </EquipmentRow>

    <div v-if="openHousing" class="pl-6">
      <div v-if="housingsOrder.length > 0" class="space-y-3">
        <HousingRow
          v-for="(housingXrefId, i) in housingsOrder"
          :key="housingXrefId"
          :housing-index="i"
          :housing="getHousing(housingXrefId)"
          :xref-pump="pump"
          :stack="stack"
          :hide-add-above="newHousingExists"
          :hide-add-below="newHousingExists"
          :hide-remove="false"
          @add-above="addHousing(i)"
          @add-below="addHousing(i + 1)"
          @remove="removeHousing($event, i)"
          @update:housings="updateHousing($event, i)"
        />
      </div>
      <div v-else class="mb-3">
        <HousingRow
          :housing="generateHousingXref()"
          :housing-index="0"
          :xref-pump="pump"
          :stack="stack"
          :hide-add-above="true"
          :hide-add-below="true"
          :hide-remove="false"
          @update:housings="updateHousing($event, 0)"
        />
      </div>
    </div>

    <transition name="vertical-slide">
      <div v-if="openCorrections" class="grid">
        <div class="col">
          <ConverterInput
            :xref-id="pump.id"
            :label="viscosityRateCorrectionLabel"
            :model-value="pump?.rate_correction_ratio"
            :base-unit="PercentageUnits.Ratio"
            :target-unit="PercentageUnits.Percentage"
            :model="PercentageUnits.options()"
            mode="write"
            @update:model-value="
              $emit('update:pumps', {
                ...pump,
                rate_correction_ratio: $event
              })
            "
          />
          <ConverterInput
            :label="recommendedViscosityRateCorrectionLabel"
            :model-value="pumpRecCorrectionInfo.recRateCorrection"
            :base-unit="PercentageUnits.Ratio"
            :target-unit="PercentageUnits.Percentage"
            :model="PercentageUnits.options()"
            :decimal-places="2"
            mode="read"
          />
        </div>
        <div class="col">
          <ConverterInput
            :label="viscosityHeadCorrectionLabel"
            :model-value="pump?.head_correction_ratio"
            :base-unit="PercentageUnits.Ratio"
            :target-unit="PercentageUnits.Percentage"
            :model="PercentageUnits.options()"
            mode="write"
            @update:model-value="
              $emit('update:pumps', {
                ...pump,
                head_correction_ratio: $event
              })
            "
          />
          <ConverterInput
            :label="recommendedViscosityHeadCorrectionLabel"
            :model-value="pumpRecCorrectionInfo.recHeadCorrection"
            :base-unit="PercentageUnits.Ratio"
            :target-unit="PercentageUnits.Percentage"
            :model="PercentageUnits.options()"
            :decimal-places="2"
            mode="read"
          />
        </div>
        <div class="col">
          <ConverterInput
            :label="viscosityBHPCorrectionLabel"
            :model-value="pump?.brake_horsepower_correction_ratio"
            :base-unit="PercentageUnits.Ratio"
            :target-unit="PercentageUnits.Percentage"
            :model="PercentageUnits.options()"
            mode="write"
            @update:model-value="
              $emit('update:pumps', {
                ...pump,
                brake_horsepower_correction_ratio: $event
              })
            "
          />
          <ConverterInput
            :label="recommendedViscosityBHPCorrectionLabel"
            :model-value="pumpRecCorrectionInfo.recBHPCorrection"
            :base-unit="PercentageUnits.Ratio"
            :target-unit="PercentageUnits.Percentage"
            :model="PercentageUnits.options()"
            :decimal-places="2"
            mode="read"
          />
        </div>
      </div>
    </transition>
    <Divider />
  </section>
</template>

<script setup lang="ts">
import type { PropType } from "vue";
import { ref, computed, provide, watch, nextTick } from "vue";
import { injectStrict, roundFloat } from "@/utils/utils";
import { computePumpLabel } from "@/models/equipment/pumps/properties";
import PumpSearchView from "@/components/equipment/pumps/PumpSearchView.vue";
import EquipmentRow from "@/components/scenarios/EquipmentRow.vue";
import AddRemoveButtons from "@/components/AddRemoveButtons.vue";
import EquipmentPanel from "@/components/scenarios/EquipmentPanel.vue";
import HousingRow from "@/components/equipment/housings/HousingRow.vue";
import FormLabel from "@/components/FormLabel.vue";
import { v4 } from "uuid";
import type { XrefHousings, XrefPumps } from "@/composables/forms/formTypes";
import ConverterInput from "@/components/ConverterInput.vue";
import { PercentageUnits } from "@/libs/units/percentage";
import { LengthUnits, LargeLengthUnits } from "@/libs/units/length";
import {
  equipmentFormKey,
  housingsFormKey,
  parametersFormKey,
  pumpsFormKey,
  scenarioCalculationsKey,
  scenarioFormKey,
  scenarioKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
// import { useStackCalculations } from "@/composables/scenarioDetailProvider/useStackCalculations";
// import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";

import AlertWrapper from "@/components/AlertWrapper.vue";
import {
  findBestHousingCombination,
  type HousingInput
} from "@/libs/calculations/pumps/pumpHousingOptimizer";
import { MassUnits } from "@/libs/units/mass";
import { useEditPermissions } from "@/composables/auth/editPermissions";
import type { PumpsQuery } from "gql/api";
import { Sizing_Pumping_Type_Enum } from "gql/api";
import InlineValue from "@/components/scenarios/InlineValue.vue";
import { useGenericNotification } from "@/composables/logger/genericNotification";
import { getPumpFieldName } from "@/composables/forms/stackAwareFields";
import { getPredictedDesiredFrequencyHz } from "@/libs/calculations/predictiveCalcs/getPredictedDesiredPumpHz";

const scenarioForm = injectStrict(scenarioFormKey, null);
const { isScenarioReadonly } = useEditPermissions(undefined, scenarioForm);
const { fireToast } = useGenericNotification();

const $emit = defineEmits<{
  (e: "add-below"): void;
  (e: "add-above"): void;
  (e: "remove", pump: XrefPumps[number]): void;
  (e: "update:pumps", pump: XrefPumps[number]): void;
  (e: "auto-add-hz-applied", payload: { pumpXrefId: string }): void;
  (e: "auto-add-started"): void;
  (e: "auto-add-finished"): void;
}>();

const props = defineProps({
  pump: {
    type: Object as PropType<XrefPumps[number]>,
    default: null
  },
  hasCompression: {
    type: Boolean,
    default: false
  },
  panelHeader: {
    type: [String, null] as PropType<string | null>,
    default: null
  },
  pumpIndex: {
    type: Number,
    default: 0
  },
  totalPumps: {
    type: Number,
    default: 1
  },
  hideAddAbove: {
    type: Boolean,
    default: false
  },
  hideAddBelow: {
    type: Boolean,
    default: false
  },
  hideRemove: {
    type: Boolean,
    default: false
  },
  disableAddAbove: {
    type: Boolean,
    default: false
  },
  disableAddBelow: {
    type: Boolean,
    default: false
  },
  disableRemove: {
    type: Boolean,
    default: false
  },
  stack: {
    type: String as PropType<"upper" | "lower" | undefined>,
    default: undefined
  }
});

provide("equipment", undefined);
provide(
  "selected-equipment-id",
  computed(() => props.pump.r_pump?.id)
);

const scenario = injectStrict(scenarioKey);
// Use stack-specific calculations - make it reactive
// const scenarioCalculations = useStackCalculations(props.stack || "upper");
const scenarioCalculations = injectStrict(scenarioCalculationsKey);
const equipmentForm = injectStrict(equipmentFormKey);
const pumpsForm = injectStrict(pumpsFormKey);
const housingsForm = injectStrict(housingsFormKey);
const parametersForm = injectStrict(parametersFormKey);

// stack-aware pump hz field (UI writes to pump_hz vs lower_pump_hz),
// but scenario calculations use `parameters.pump_hz`, so we will also force that.
const pumpHzField = computed(() => getPumpFieldName("pumpHz", props.stack));

const pumpFlowRate = computed(() => {
  const baseFlowRate =
    scenarioCalculations.fluidProperties
      .fluidPropertiesAfterAllGasSeparationAtPump.value.fluidFlowRateBBLD;

  // Last (bottom) row uses base flow rate after gas separation.
  if (props.pumpIndex === props.totalPumps - 1) {
    return baseFlowRate;
  }

  const individualPumpCalcs =
    scenarioCalculations.pumpCalculations.individual.value;
  const orderKey =
    props.stack === "lower" ? "lower_pumps_order" : "pumps_order";
  const pumpsOrder =
    (equipmentForm.data.value[orderKey] as string[] | undefined) || [];

  // Walk down rows below this one until we find a selected pump with valid
  // discharge flow calculations. If none exist, fall back to base flow rate.
  for (
    let pumpCalcIndex = props.pumpIndex + 1;
    pumpCalcIndex < pumpsOrder.length;
    pumpCalcIndex++
  ) {
    const nextLowerPumpId = pumpsOrder[pumpCalcIndex];
    const nextLowerPump = nextLowerPumpId
      ? pumpsForm.data.value[nextLowerPumpId]
      : undefined;

    if (!nextLowerPump?.id || !nextLowerPump.r_pump) {
      continue;
    }

    const pumpCalc = individualPumpCalcs[nextLowerPump.id];
    const nextFlowRate =
      pumpCalc?.dischargeStageCalculations?.dischargeFluidPropertiesContext
        ?.flowRatesInSitu?.fluidFlowRateBBLD;

    if (typeof nextFlowRate === "number" && Number.isFinite(nextFlowRate)) {
      return nextFlowRate;
    }
  }

  return baseFlowRate;
});

const isHps = computed(() => {
  return (
    scenarioForm?.data.value.pumping_type === Sizing_Pumping_Type_Enum.Hps ||
    scenario.value?.pumping_type === Sizing_Pumping_Type_Enum.Hps
  );
});

// Default filter state: enabled for HPS, disabled for ESP
const defaultEnableFilter = computed(() => {
  return isHps.value;
});

// Required pump stages for this specific pump
const requiredPumpStages = computed(() => {
  if (!props.pump?.id) return 0;
  const pumpCalc =
    scenarioCalculations.pumpCalculations.individual.value[props.pump.id];
  return pumpCalc?.requiredStages > 0 ? pumpCalc.requiredStages : 0;
});

// Total selected pump stages across all pumps in the stack
const totalSelectedPumpStages = computed(() => {
  return scenarioCalculations.descendingOrderPumps.value.reduce(
    (sum, pump) => sum + (pump.total_stages || 0),
    0
  );
});

// Computed labels for compact mode abbreviations
const isCompactMode = computed(() => !!props.stack);

const viscosityCorrectionLabel = computed(() =>
  isCompactMode.value ? "Visc Corr." : "Viscosity Correction"
);

const viscosityRateCorrectionLabel = computed(() =>
  isCompactMode.value ? "Visc Rate Corr." : "Viscosity Rate Correction"
);

const recommendedViscosityRateCorrectionLabel = computed(() =>
  isCompactMode.value
    ? "Rec. Visc Rate Corr."
    : "Recommended Viscosity Rate Correction"
);

const viscosityHeadCorrectionLabel = computed(() =>
  isCompactMode.value ? "Visc Head Corr." : "Viscosity Head Correction"
);

const recommendedViscosityHeadCorrectionLabel = computed(() =>
  isCompactMode.value
    ? "Rec. Visc Head Corr."
    : "Recommended Viscosity Head Correction"
);

const viscosityBHPCorrectionLabel = computed(() =>
  isCompactMode.value ? "Visc BHP Corr." : "Viscosity BHP Correction"
);

const recommendedViscosityBHPCorrectionLabel = computed(() =>
  isCompactMode.value
    ? "Rec. Visc BHP Corr."
    : "Recommended Viscosity BHP Correction"
);

const showCorrectionsLabel = computed(() =>
  isCompactMode.value ? "Show Corr." : "Show Corrections"
);

const useHighStrengthShaftLabel = computed(() =>
  isCompactMode.value ? " Use HS Shaft" : " Use High Strength Shaft"
);

const ratioIsCorrection = (correction: number | undefined | number) => {
  return correction !== null && correction !== undefined && correction !== 1;
};

const hasRateCorrection = computed(() =>
  ratioIsCorrection(props.pump?.rate_correction_ratio)
);

const hasHeadCorrection = computed(() =>
  ratioIsCorrection(props.pump?.head_correction_ratio)
);

const hasBrakeHorsepowerCorrection = computed(() =>
  ratioIsCorrection(props.pump?.brake_horsepower_correction_ratio)
);

const hasCorrections = computed(
  () =>
    !!props.pump &&
    (hasRateCorrection.value ||
      hasHeadCorrection.value ||
      hasBrakeHorsepowerCorrection.value)
);

const openCorrections = ref(hasCorrections.value);

const pumpRecCorrectionInfo = computed(() => {
  // Access the reactive calculations to ensure reactivity tracking
  // Narrow the type since stack is "upper" | "lower" | undefined, not "both"
  // const calcs = scenarioCalculations.value as ScenarioCalculations;
  // const individualPumpCalculations = calcs.pumpCalculations.individual;
  // const individualPumpCalc = individualPumpCalculations.value[props.pump?.id];

  if (!scenarioCalculations.pumpCalculations.individual.value[props.pump.id]) {
    return {
      recBHPCorrection: 1,
      recHeadCorrection: 1,
      recRateCorrection: 1
    };
  }

  const { recBHPCorrection, recHeadCorrection, recRateCorrection } =
    scenarioCalculations.pumpCalculations.individual.value[props.pump.id];

  return {
    recBHPCorrection,
    recHeadCorrection,
    recRateCorrection
  };
});

const useHighStrengthShaft = ref(props.pump.use_high_strength_shaft || false);

// Sync ref from prop when pump changes (e.g. after re-selection) so checkbox reflects stored value
watch(
  () => [props.pump?.id, props.pump?.use_high_strength_shaft] as const,
  ([, nextShaft]) => {
    useHighStrengthShaft.value = nextShaft ?? false;
  }
);

watch(
  useHighStrengthShaft,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      $emit("update:pumps", {
        ...props.pump,
        use_high_strength_shaft: newValue
      });
    }
  },
  { deep: true }
);

const pumpLabel = computed(() =>
  computePumpLabel(props.pump?.r_pump, useHighStrengthShaft.value)
);

const housingsOrder = computed(() => {
  // Primary source of truth: explicit housings_order on the pump, filtered
  // to only include ids that still exist in the housings form.
  const filteredPumpOrder =
    props.pump?.housings_order?.filter(
      (housingId: string) => housingsForm.data.value[housingId]
    ) || [];

  if (filteredPumpOrder.length > 0) {
    return filteredPumpOrder;
  }

  // Fallback: on first auto-add we may have created housings in the form
  // before housings_order was fully synchronized. In that case, derive the
  // order from all housings belonging to this pump.
  if (!props.pump?.id) {
    return [];
  }

  return Object.values(housingsForm.data.value)
    .filter(
      (housing: XrefHousings[number]) =>
        housing.scenario_equipment_xref_pump_id === props.pump?.id &&
        !housing.journal_deleted_at
    )
    .map(housing => housing.id);
});

/**
 * Pick the best housing type to meet stagesNeeded: minimizes overshoot (total
 * stages added vs stages needed), then prefers larger stage_count (fewer housings).
 * If none fit (all stage_count > stagesNeeded), pick smallest.
 */
function pickBestHousingForStages(
  availableHousings: NonNullable<PumpsQuery["pumps"][number]["r_housings"]>,
  stagesNeeded: number
): (typeof availableHousings)[number] | undefined {
  if (availableHousings.length === 0) return undefined;
  const withCount = availableHousings.map(h => ({
    h,
    stage_count: h.stage_count || 1
  }));
  const thatFits = withCount.filter(
    ({ stage_count }) => stage_count <= stagesNeeded
  );
  if (thatFits.length === 0) {
    const byAsc = [...withCount].sort((a, b) => a.stage_count - b.stage_count);
    return byAsc[0].h;
  }
  const withOvershoot = thatFits.map(({ h, stage_count }) => {
    const housingsNeeded = Math.ceil(stagesNeeded / stage_count);
    const totalStages = housingsNeeded * stage_count;
    const overshoot = totalStages - stagesNeeded;
    return { h, stage_count, overshoot };
  });
  withOvershoot.sort(
    (a, b) => a.overshoot - b.overshoot || b.stage_count - a.stage_count
  );
  return withOvershoot[0].h;
}

/**
 * Auto-add housings to match required stages.
 * Uses optimizer to find a combination that hits required stages, minimizes
 * number of housings and housing types, and minimizes overshoot. Falls back to
 * single-type repeat if optimizer returns no solution.
 */
function autoAddHousingsForRequiredStages(
  pumpXref: XrefPumps[number],
  selectedPumpEquipment: PumpsQuery["pumps"][number],
  requiredStages: number
): void {
  if (requiredStages <= 0) {
    return;
  }

  const availableHousings = selectedPumpEquipment.r_housings || [];
  if (availableHousings.length === 0) {
    return;
  }

  const currentHousingsOrder = pumpXref.housings_order || [];
  const currentTotalStages = currentHousingsOrder.reduce((sum, housingId) => {
    const housing = getHousing(housingId);
    return sum + (housing?.stage_count || 0);
  }, 0);
  const stagesNeeded = requiredStages - currentTotalStages;

  if (stagesNeeded <= 0) {
    return;
  }

  const newHousingIds: string[] = [...currentHousingsOrder];
  const newHousings: Record<string, XrefHousings[number]> = {};

  const choices = findBestHousingCombination(
    availableHousings as HousingInput[],
    stagesNeeded,
    { toleranceStages: 0 }
  );

  if (choices.length > 0) {
    for (const { housing, count } of choices) {
      for (let i = 0; i < count; i++) {
        const housingXref = generateHousingXref();
        newHousingIds.push(housingXref.id);
        const sc = Math.max(1, housing.stage_count ?? 1);
        const len = housing.length_inches ?? 0;
        newHousings[housingXref.id] = {
          ...housingXref,
          housing_id: housing.id,
          r_housing: housing as (typeof availableHousings)[number],
          length_inches: len,
          stage_count: sc
        };
      }
    }
  } else {
    // Fallback: single housing type, repeat to meet stages
    const defaultHousing = pickBestHousingForStages(
      availableHousings,
      stagesNeeded
    );
    if (!defaultHousing) {
      fireToast({
        severity: "warn",
        summary: "Could not add housings",
        detail:
          "No valid housing combination could be found for the required stages. Please select housings manually.",
        life: 8000
      });
      return;
    }
    const stagesPerHousing = defaultHousing.stage_count || 1;
    const housingsNeeded = Math.ceil(stagesNeeded / stagesPerHousing);
    for (let i = 0; i < housingsNeeded; i++) {
      const housingXref = generateHousingXref();
      newHousingIds.push(housingXref.id);
      newHousings[housingXref.id] = {
        ...housingXref,
        housing_id: defaultHousing.id,
        r_housing: defaultHousing,
        length_inches: defaultHousing.length_inches || 0,
        stage_count: Math.max(1, defaultHousing.stage_count || 1)
      };
    }
  }

  housingsForm.update(newHousings);

  const updatedTotalStages = newHousingIds.reduce((sum, housingId) => {
    const housing = newHousings[housingId] || getHousing(housingId);
    return sum + (housing?.stage_count || 0);
  }, 0);

  const updatedLengthFeet = newHousingIds.reduce((sum, housingId) => {
    const housing = newHousings[housingId] || getHousing(housingId);
    const lengthInches = housing?.length_inches || 0;
    return sum + (LengthUnits.Inches.toStandardUnit(lengthInches) || 0);
  }, 0);

  $emit("update:pumps", {
    ...pumpXref,
    housings_order: newHousingIds,
    total_stages: updatedTotalStages,
    length_feet: roundFloat(updatedLengthFeet),
    weight_pounds: getAccumulatedOrderedHousingWeight(newHousingIds)
  });
}

async function handlePumpSelect(
  xrefPump: XrefPumps[number],
  payload: { pump: PumpsQuery["pumps"][number]; autoAddHousings: boolean },
  toggle: () => void
) {
  const selectedPumpEquipment = payload.pump;
  const shouldAutoAddHousings = payload.autoAddHousings && isHps.value;

  toggle();
  openHousing.value = true;
  housingsOrder.value.forEach(housingId => {
    const housing = getHousing(housingId);
    if (housing) {
      housingsForm.update({
        [housing.id]: {
          ...housing,
          journal_deleted_at: "now()" // Mark as deleted
        }
      });
    }
  });

  const updatedPump: XrefPumps[number] = {
    ...xrefPump,
    id: xrefPump?.id,
    pump_id: selectedPumpEquipment.id,
    r_pump: selectedPumpEquipment,
    housings_order: [],
    total_stages: 0,
    length_feet: 0,
    weight_pounds: 0,
    use_high_strength_shaft: isHps.value
      ? xrefPump.use_high_strength_shaft ?? true
      : xrefPump.use_high_strength_shaft
  };

  // First, update the pump selection itself.
  $emit("update:pumps", updatedPump);

  // If auto-add housings is enabled for HPS, wait for calculations and add housings
  // based on the freshly selected pump.
  if (shouldAutoAddHousings) {
    $emit("auto-add-started");
    const prevPumpHz = parametersForm.data.value.pump_hz;
    const prevStackPumpHz = parametersForm.data.value[pumpHzField.value];
    let foundHz: number | null = null;
    try {
      parametersForm.update({
        pump_hz: 60,
        [pumpHzField.value]: 60
      });

      const didAutoAdd = await handleAutoAddHousingsForPump(
        updatedPump,
        selectedPumpEquipment
      );

      if (didAutoAdd) {
        foundHz = await calculateHzAfterAutoAdd();
      }
    } finally {
      if (foundHz === null) {
        parametersForm.update({
          pump_hz: prevPumpHz,
          [pumpHzField.value]: prevStackPumpHz
        });
      } else {
        // Dual-stack: scenario calculations always use `pump_hz`, even in lower stack.
        // Ensure both fields are deterministic.
        parametersForm.update({
          pump_hz: foundHz,
          [pumpHzField.value]: foundHz
        });
        $emit("auto-add-hz-applied", {
          pumpXrefId: updatedPump.id
        });
      }
      $emit("auto-add-finished");
    }
  }
}

async function calculateHzAfterAutoAdd(): Promise<number | null> {
  const maxReadyAttempts = 50; // ~5s
  let readyAttempts = 0;
  while (readyAttempts < maxReadyAttempts) {
    const maxFlow = scenarioCalculations.maxFlowRateAtIntakeBBLD.value;
    if (typeof maxFlow === "number" && Number.isFinite(maxFlow)) {
      break;
    }
    await nextTick();
    await new Promise(resolve => setTimeout(resolve, 100));
    readyAttempts++;
  }

  const maxAttempts = 3;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (attempt > 0) {
      await nextTick();
      await new Promise(resolve => setTimeout(resolve, 200));
    }

    const { status, targetRow } = getPredictedDesiredFrequencyHz(
      scenarioCalculations,
      parametersForm.data.value
    );

    if (status === "result-found" || status === "threshold-found") {
      const hypotheticalFrequencyHz = targetRow?.hypotheticalFrequencyHz || 0;
      const foundHz = Math.round(hypotheticalFrequencyHz * 10 ** 6) / 10 ** 6;
      return foundHz;
    }
  }

  return null;
}

/**
 * Auto-add housings for the given pump/equipment. This mirrors the
 * behavior of the old "Add Housings" button but operates directly on
 * the freshly selected pump so it also works on first selection.
 *
 * This function polls for calculations to become available before
 * adding housings, ensuring we use the optimized housing selection
 * algorithm based on requiredStages.
 */
async function handleAutoAddHousingsForPump(
  pumpXref: XrefPumps[number],
  selectedPumpEquipment: PumpsQuery["pumps"][number]
): Promise<boolean> {
  // Poll for calculations to be ready
  const maxAttempts = 50; // 5 seconds max (50 * 100ms) (may be able to remove this but it acts as a fallback in case of timing issues)
  let attempts = 0;

  while (attempts < maxAttempts) {
    const pumpCalc =
      scenarioCalculations.pumpCalculations.individual.value[pumpXref.id];

    if (
      pumpCalc &&
      pumpCalc.requiredStages !== undefined &&
      pumpCalc?.xrefPump?.r_pump?.id === selectedPumpEquipment.id
    ) {
      // Calculations are ready! Proceed with optimal housing selection
      let requiredStages = pumpCalc.requiredStages || 0;
      if (requiredStages <= 0) {
        requiredStages = 1;
      }

      autoAddHousingsForRequiredStages(
        pumpXref,
        selectedPumpEquipment,
        requiredStages
      );
      // Let parent run Calc Hz immediately after housings are added.
      // Waiting for a second calc synchronization gate can miss the first-add path.
      return true;
    }

    // Wait 100ms before checking again (same here just a fallback to attempt to auto add to the best we can)
    await new Promise(resolve => setTimeout(resolve, 100));
    attempts++;
  }

  // Timeout - calculations never arrived
  fireToast({
    severity: "warn",
    summary: "Auto-add Housings Timeout",
    detail:
      "Pump calculations did not become available in time. Please manually add housings for this pump.",
    life: 5000
  });

  return false;
}

const newHousingExists = computed(() => {
  // Check if there are no housings at all
  if (housingsOrder.value?.length === 0) {
    return true;
  }
  // Check if any housings have null housing_id (indicating incomplete configuration)
  return housingsOrder.value.some((housingId: string) => {
    const housing = housingsForm.data.value[housingId];
    return housing && housing.housing_id === null;
  });
});

const pumpHasAvailableHousings = computed(() => {
  return (props.pump?.r_pump?.r_housings?.length || 0) > 0;
});

const getAccumulatedOrderedHousingWeight = (
  housingIds: Array<string> | undefined
) => {
  return (
    housingIds?.reduce((totalWeight: number, housingId: string) => {
      const housing = getHousing(housingId);
      return totalWeight + (housing?.r_housing?.weight_pounds || 0);
    }, 0) || 0
  );
};

watch(pumpHasAvailableHousings, newValue => {
  if (newValue) {
    $emit("update:pumps", {
      ...props.pump,
      weight_pounds: getAccumulatedOrderedHousingWeight(
        props.pump.housings_order as string[]
      )
    });
  }
});

const openHousing = ref(pumpHasAvailableHousings.value);

function generateHousingXref(): XrefHousings[number] {
  return {
    __typename: "scenario_equipment_xref_housings",
    id: v4(),
    scenario_equipment_xref_pump_id: props.pump?.id,
    housing_id: null,
    length_inches: 0,
    stage_count: 0,
    winning_revision_id: null,
    winning_revision_parent_id: null,
    winning_revision_created_at: "now()",
    winning_revision_updated_at: "now()",
    winning_revision_deleted_at: null,
    r_housing: null
  } as XrefHousings[number];
}

function addHousing(index: number): XrefHousings[number] {
  const housing = generateHousingXref();
  const housingIndex = index < 0 ? 0 : index;
  const newOrder = Object.values(props.pump.housings_order || []);
  newOrder.splice(housingIndex, 0, housing.id);

  const updatedTotalStages =
    (props.pump?.total_stages || 0) + (housing?.stage_count || 0);
  const updatedLengthFeet =
    (props.pump?.length_feet || 0) +
    (LengthUnits.Inches.toStandardUnit(housing?.length_inches) || 0);

  housingsForm.update({
    [housing.id]: housing
  });
  $emit("update:pumps", {
    ...props.pump,
    total_stages: updatedTotalStages,
    length_feet: roundFloat(updatedLengthFeet),
    housings_order: newOrder,
    weight_pounds: getAccumulatedOrderedHousingWeight(newOrder)
  });
  return housing;
}

function removeHousing(housing: XrefHousings[number], index: number): void {
  const newOrder = Object.values(props.pump.housings_order || []);
  const housingToBeRemoved = getHousing(newOrder[index]);

  newOrder.splice(index, 1);
  if (housingsForm.initialData.value?.[housing.id]) {
    housingsForm.update({
      [housing.id]: { ...housing, journal_deleted_at: "now()" }
    });
  } else {
    delete housingsForm.data.value[housing.id];
  }

  const updatedTotalStages =
    (props.pump?.total_stages || 0) - (housingToBeRemoved?.stage_count || 0);
  const updatedLengthFeet =
    (props.pump?.length_feet || 0) -
    (LengthUnits.Inches.toStandardUnit(housingToBeRemoved?.length_inches) || 0);

  $emit("update:pumps", {
    ...props.pump,
    total_stages: updatedTotalStages,
    length_feet: roundFloat(updatedLengthFeet),
    housings_order: newOrder,
    weight_pounds: getAccumulatedOrderedHousingWeight(newOrder)
  });
}

function updateHousing(housing: XrefHousings[number], index: number): void {
  const newOrder = Object.values(props.pump.housings_order || []);
  const housingToBeUpdated = getHousing(newOrder[index]);

  newOrder.splice(index, 1, housing.id);

  const updatedTotalStages =
    (props.pump?.total_stages || 0) -
    (housingToBeUpdated?.stage_count || 0) +
    (housing.stage_count || 0);
  const updatedLengthFeet =
    (props.pump?.length_feet || 0) -
    (LengthUnits.Inches.toStandardUnit(housingToBeUpdated?.length_inches) ||
      0) +
    (LengthUnits.Inches.toStandardUnit(housing.length_inches) || 0);

  housingsForm.update({
    [housing.id]: housing
  });
  $emit("update:pumps", {
    ...props.pump,
    housings_order: newOrder,
    total_stages: updatedTotalStages,
    weight_pounds: getAccumulatedOrderedHousingWeight(newOrder),
    length_feet: roundFloat(updatedLengthFeet)
  });
}

function getHousing(id: string): XrefHousings[number] {
  return housingsForm.data.value[id];
}
</script>

<style lang="scss" scoped>
@media only screen and (max-width: 750px) {
  .mobile-wrap {
    flex-wrap: wrap;
    gap: 0.5rem;
  }
}

.pump-label-wrapper {
  max-height: 100%;
  min-height: 100%;
  height: 100%;
  display: flex;
  position: relative;
  flex-direction: row;
  align-items: center;
}

.pump-badge-outer {
  position: relative;
}

.pump-badge-inner {
  height: 100%;
  display: flex;
  flex-direction: column;
  position: absolute;
  justify-content: space-evenly;
  left: 5px;
  height: 60px;
  max-height: 54px;
  bottom: -17px;
}

.pump-badge {
  margin-left: 3px;
  font-size: 12px;
  line-height: 10px;
  padding-left: 5px;
  padding-right: 5px;
  padding-bottom: 0px;
  padding-top: 0px;
  height: 0.7rem;
}

.field-checkbox {
  label {
    margin-left: 0.5rem;
  }
}
</style>
